import { api, writeDB } from './store';
import { Venue, VenuePlan, VenueLayout, SeatStatus, FlatVenuePlanSection } from './venue';
import { TimeFrame, delay, formatDate } from '../util/util';
import { Company } from './company';
import { history } from './history';
import { eventPath } from '../routes';
import { Logo } from './logo';
import { Image, FileRepresenting } from './asset';
import { Ownership } from './ownerships';
import { GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom, usePolling } from './data';
import { Allocation, parseSeatStatuses, Contingent } from './contingent';
import { ProductVariant } from './product';
import { apollo } from './graphql';
import { GET_EVENT_STATISTICS } from './queries/pricingStats';
import { CHANGE_EVENT_VENUE_LAYOUT } from './queries/changeEventVenueLayout';
import { PrintedTicket } from './ticket';
import { PaginatedResponse } from './api';
import { JSONObject } from 'k6';
import {
  GET_EVENT_SCANNER_CODE_TYPES,
  GET_EVENT_SCANNER_NAMES,
  UPDATE_EVENT_SCANNER_CODE_TYPES,
  UPDATE_EVENT_SCANNER_NAMES,
} from './queries/event';
import { OrderedCategory } from './category';
import { intersection } from 'lodash';
import { EventScannerCodeType, ShippingRequirement } from '../generated/graphql';

/* INTERFACES */
export type EventStatus = 'on_sale' | 'draft' | 'in_review' | 'stopped';

export type MetaSettings = JSONObject & {
  personalIdentifierDisclaimer: string | null;
  forcePrintPersonalization?: boolean;
};

export interface TicketLine {
  title?: string | null;
  titleDe?: string | null;
  titleEn?: string | null;
  description?: string | null;
  descriptionDe?: string | null;
  descriptionEn?: string | null;
}

export type PersonalizationRequirement =
  | 'no_personalization_required'
  | 'group_personalization_required'
  | 'ticket_personalization_required'
  | 'contact_tracing_required'
  | 'check_in_required'
  | 'bdkv';

export const EVENT_SCANNER_CODE_TYPES = ['ean', 'qr'] as const;

export interface SaleStatistics {
  productVariantDescription: string;
  brutPrice: number;
  price: number;
  count: number;
  physical: boolean;
  pricingCategoryName: string;
  total: number;
}

export interface EventBase {
  id: string;
  legacyId?: string;
  name?: string | null;
  nameOverride?: string;
  salesStats?: EventSalesStats;
  ageLimit?: number;
  artist?: string | null;
  artistOverride?: string;
  owningCompany: Company;
  ownerships?: Ownership[];
  ticketSupportLine?: string;
  ticketSupportLineOverride?: string;
  sku: string;
  slug: string;
  externalReference?: string | null;
  status: EventStatus;
  tourOrganizer?: string;
  ticketHeadline?: string;
  additionalInformation?: TicketLine;
  ticketHeadlineOverride?: string;
  additionalInformationOverride?: string;
  immediatePaymentAtStockQuota: number | null;
  localOrganizer?: string;
  doorTime?: string;
  stageTime?: string;
  venueLayout: VenueLayout;
  venuePlan?: VenuePlan;
  allocations?: Allocation[];
  contingents?: Contingent[];
  seatStatus: { [seatId: string]: SeatStatus };
  $permissions: Array<
    | 'update_base_attributes'
    | 'update_shop_settings'
    | 'update_owner'
    | 'update_venue'
    | 'reactivate'
    | 'request_review'
    | 'stop_sale'
    | 'update_sold_out_message'
    | 'start_sale'
    | 'update_categories'
    | 'update_pricing_categories'
    | 'create_clearing'
    | 'destroy'
    | 'update_local_pricing_categories'
    | 'update_resale_settings'
  >;
  soldOutMessage?: string;
  logos: Logo[];
  meta: MetaSettings;
  logoIds?: string[];
  owningCompanyId?: string;
  categories: OrderedCategory[];
  happeningFrom: Date;
  happeningUntil?: Date;
  personalizationRequirement?: PersonalizationRequirement | null;
  publishedFrom?: Date;
  publishedUntil?: Date;
  venue?: Venue;
  venueLayoutId?: string;
  ticketCodesDownloadLink?: string;
  fullTicketCodesDownloadLink?: string;
  allocationPdfDownloadUrl: string;
  salesReportPdfDownloadUrl: string;
  pricingCategories: PricingCategory[];
  description?: string | null;
  descriptiveName?: string;
  shippableFrom?: Date;
  productVariants?: ProductVariant[];
  clearingOrderId?: string;
  publicShippableFrom?: Date;
  dhlShippingFrom?: Date;
  recallDetails?: ProductRecall;
  pricingCategoryStatistics: SaleStatistics[];
  ticketLayout?: string;
  onlineTicketLayout?: string;
  localTicketLayout?: string;
  checkInStartAt?: Date;
  checkInEndAt?: Date;
  cartLimit?: number;
  scannerNames?: string[];
  excludeFromFeeds?: boolean;
  blockAllSeats?: boolean;
  shippingRequirements?: ShippingRequirement[];
  freeShippingMode?: 'OFF' | 'UNANIMOUS' | 'FORCE';
  prohibitedShippingTags?: string[];
  scannerCodeTypes?: EventScannerCodeType[];
  publicTransportDescription?: string | null;
}

export interface Event extends EventBase {
  ticketHeaderImage?: Image;
  image?: Image | null;
  printedTickets?: PrintedTicket[];
  secureId: string;
}

export interface EventEdit extends EventBase {
  ticketHeaderImage?: string | null;
  productVariantsActiveFrom?: Date | null;
}

export interface EventCreate extends Event {
  launchSaleAt?: Date;
}

export interface PricingCategory {
  id: string;
  eventId: string;
  name: string;
  $permissions: Array<'update' | 'destroy'>;
  productVariants: EventProductVariant[];
}

export interface EventProductVariant {
  id: string;
  sku?: string;
  local: boolean;
  description: string;
  basePrice: number;
  presaleFee: number;
  presaleFeePercentage?: number;
  systemFee: number;
  physical: boolean;
  taxRate: number;
  priceParts: PricePart[];
  _delete?: boolean;
  stockCount?: number;
  availableCount?: number;
  reservedCount?: number;
  minimumStockCount?: number;
  activeFrom?: Date;
  activeUntil?: Date;
  private?: boolean;
  pricingCategoryId?: string;
  categoryName?: string;
  refundDeduction?: number;
  published?: boolean;
  buyerFee: number | null;
  sellerFee: number | null;
  resaleAllowed: boolean;
}

export interface PricePart {
  description: string;
  price: number;
}

export interface EventSalesStats {
  processing: number;
  available: number;
  blocked: number;
  cart: number;
  local: number;
  personalized: number;
  reserved: number;
  returned: number | null;
  systemFree: number;
  systemSold: number;
  soldHard: number;
  soldOnline: number;
  sold: number;
  used: number;
  unused?: number;
}

export interface ProductRecall {
  id?: string;
  titleEn?: string;
  titleDe?: string;
  textEn?: string;
  textDe?: string;
  emailTemplateEn?: string;
  emailTemplateDe?: string;
  emailSubjectEn?: string;
  emailSubjectDe?: string;
  triggeredAt?: Date;
  paymentsMassBookable?: boolean;
  usedTicketsMassRefundable?: boolean;
  paused?: boolean;
}

export type EventRecallDetails = ProductRecall & { productId: string };

/* EVENTS: CREATE */
export async function addEvent(event: Partial<EventCreate>): Promise<void> {
  const response = await api.createEvent(event);
  writeDB.table('events').insert(response.data);
  history.push(eventPath(response.data.slug));
}

/* EVENTS: READ */
export async function getRawEvents({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Event[]>> {
  const response = await api.indexEvents({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as unknown as PaginatedResponse<Event[]>;
}

export async function getEvents({
  term,
  page,
  perPage,
  status,
  timeframe,
  sortBy = 'happening_from',
  sortDir = 'desc',
}: {
  term?: string;
  page?: number;
  perPage?: number;
  status?: EventStatus[];
  timeframe?: TimeFrame;
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexEvents({
    page: (page || 0) + 1,
    perPage,
    term,
    status,
    ...(timeframe && timeframe[0] && timeframe[1]
      ? {
          happeningFrom: timeframe[0],
          happeningUntil: timeframe[1],
        }
      : {}),
    sortBy,
    sortDir,
  });
  writeDB.table('events').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getRawEvent(eventId: string): Promise<PaginatedResponse<Event>> {
  const response = await api.showEvent({ eventId });
  return response as unknown as PaginatedResponse<Event>;
}

export async function getEventDetails({
  eventId,
  includeCategories = true,
}: {
  eventId: string;
  includeCategories?: boolean;
}): Promise<void> {
  const event = (await api.showEvent({ eventId })).data;

  writeDB.table('events').upsert({
    ...event,
    categoryProducts: includeCategories
      ? await api.indexCategoryAssignments({
          productId: eventId,
        })
      : undefined,
  });
}

export async function getEventSalesStatus(eventId: string, { force }: { force?: boolean } = {}): Promise<void> {
  if (!force && writeDB.table('events').find(eventId)?.salesStats) return;
  const response = await api.showEventSalesStats({ eventId });
  writeDB.table('events').update(eventId, { salesStats: response.data as EventSalesStats });
}

export function useEventSalesStatusPolling(
  eventId: string,
  { start }: { start: boolean } = { start: false }
): [boolean, () => void] {
  return usePolling(
    () => {
      getEventSalesStatus(eventId, { force: true });
    },
    15_000,
    { start }
  );
}

export function getEventName(
  event?: Partial<Event>,
  options?: {
    sku?: boolean;
    venueName?: boolean;
    city?: boolean;
    happeningFrom?: boolean;
  }
): string {
  if (!event) {
    return '';
  }
  return [
    options?.sku && event.sku,
    event.artist,
    event.name,
    options?.venueName && event.venue?.name,
    options?.city && event.venue?.address?.city,
    [options?.happeningFrom && formatDate(event.happeningFrom), event.stageTime].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(' - ');
}

export async function getEventScannerNames(eventId: string): Promise<void> {
  const result = await apollo.query({
    query: GET_EVENT_SCANNER_NAMES,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  if (!result.data?.event) return;
  writeDB.table('events').update(eventId, result.data.event);
}

export async function getEventScannerCodeTypes(eventId: string): Promise<void> {
  const result = await apollo.query({
    query: GET_EVENT_SCANNER_CODE_TYPES,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });
  if (result.data.event) {
    writeDB.table('events').update(eventId, result.data.event);
  }
}

/* EVENTS: UPDATE */
export async function changeOwner(event: Event, company: Company): Promise<void> {
  writeDB.table('events').update(event, { owningCompany: company });
  await delay(2);
}

export async function updateEventImage({
  eventId,
  image,
}: {
  eventId: string;
  image?: FileRepresenting | null;
}): Promise<void> {
  if (image === undefined) {
    return;
  }
  const response = await api.updateEvent({ eventId, image });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Event;
  writeDB.table('events').update(eventId, data);
}

export async function updateEventDetails({
  eventId,
  changes,
}: {
  eventId: string;
  changes: Partial<EventEdit>;
}): Promise<void> {
  const response = await api.updateEvent({
    eventId,
    ...changes,
    slug: changes.slug ? changes.slug.trim() : undefined,
  });

  // TODO: Update api model to match local interface
  const data = response.data as unknown as Event;
  writeDB.table('events').update(eventId, data);
}

export async function changeEventVenueLayout({
  event,
  venueLayoutId,
}: {
  event: Event;
  venueLayoutId: string;
}): Promise<void> {
  const response = await apollo.mutate({
    mutation: CHANGE_EVENT_VENUE_LAYOUT,
    variables: {
      eventId: event.id,
      venueLayoutId,
    },
  });
  if (response.data?.changeEventVenueLayout?.event) {
    const data = response.data.changeEventVenueLayout.event;
    writeDB.table('events').update(event, {
      venue: { ...data.venue, $permissions: [], venueLayouts: [], events: [] },
      venueLayoutId: data.venueLayoutId,
      venueLayout: { ...data.venueLayout, $permissions: [] },
    });
  }
}

export async function updateEventScannerNames(eventId: string, scannerNames: string[]): Promise<void> {
  const response = await apollo.mutate({
    mutation: UPDATE_EVENT_SCANNER_NAMES,
    variables: { eventId, scannerNames },
  });
  if (response.data?.updateScannerNames?.event) {
    writeDB.table('events').update(eventId, response.data.updateScannerNames.event);
  }
}

export async function updateEventScannerCodeTypes(
  eventId: string,
  scannerCodeTypes: EventScannerCodeType[]
): Promise<void> {
  const response = await apollo.mutate({
    mutation: UPDATE_EVENT_SCANNER_CODE_TYPES,
    variables: { eventId, scannerCodeTypes: intersection(scannerCodeTypes, EVENT_SCANNER_CODE_TYPES) },
  });
  if (response.data?.updateEventScannerCodeTypes) {
    writeDB.table('events').update(eventId, response.data.updateEventScannerCodeTypes.event);
  }
}

export function increaseEventUnusedTicketsStats(event: Event, newCodesCount: number): void {
  const original = event.salesStats;
  if (!original) return;

  const newStats: EventSalesStats = {
    ...original,
    unused: (original.unused ?? 0) + newCodesCount,
  };
  writeDB.table('events').update(event.id, { salesStats: newStats });
}

export function locallyUpdateEventTicketsStats(event: Event, modifications: Partial<EventSalesStats>): void {
  const original = event.salesStats;
  if (!original) return;

  const newStats: EventSalesStats = {
    ...original,
    unused: modifications?.['unused_codes_count'] ?? 0,
    used: modifications?.['used_codes_count'] ?? 0,
  };
  writeDB.table('events').update(event.id, { salesStats: newStats });
}

/* EVENTS: DELETE */
export async function deleteEvent(eventId: string): Promise<void> {
  await api.destroyEvent({ eventId });
  writeDB.table('events').delete(eventId);
}

export async function getProductRecall(productId: string): Promise<void> {
  const productRecall = await api.showProductRecall({ productId });
  writeDB.table('events').update(productId, { recallDetails: productRecall.data });
}

export async function addProductRecall(productRecallDetails: EventRecallDetails): Promise<void> {
  const productRecall = await api.createProductRecall(productRecallDetails);

  writeDB.table('events').update(productRecallDetails.productId, {
    recallDetails: productRecall.data,
  });
}

export async function updateProductRecall(productRecallDetails: EventRecallDetails): Promise<void> {
  const productRecall = await api.updateProductRecall(productRecallDetails);
  writeDB.table('events').update(productRecallDetails.productId, {
    recallDetails: productRecall.data,
  });
}

export async function triggerMassRecall(eventId: string): Promise<void> {
  await api.triggerMassRefund({ eventId });
  await getProductRecall(eventId);
}

export async function getBackPaymentLinks(): Promise<{
  bankTransfer?: string;
  paypal?: string;
  bankTransferAndBook?: string;
  paypalAndBook?: string;
}> {
  const response = await api.getDownloadUrlsForPendingBackPayments();
  return response.data;
}

export async function loadEventStatistics({ id }: { id: string }): Promise<void> {
  const result = await apollo.query({
    query: GET_EVENT_STATISTICS,
    variables: { id },
    fetchPolicy: 'network-only',
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pricingCategoryStatistics = (result.data?.product as any)?.pricingCategoryStatistics ?? [];
  writeDB.table('events').update(id, {
    pricingCategoryStatistics,
  });
}

export async function getEventWithFlatSeats(eventId: string): Promise<FlatVenuePlanSection | null> {
  const event = (await api.showEvent({ eventId })).data;
  if (event.venueLayout) {
    const seatMap = (
      await api.showSeatmap({
        venueLayoutId: event.venueLayout.id,
      })
    ).data;
    const eventSeatStatus = parseSeatStatuses(
      (await api.indexSeatStatuses({ eventId: event.id })).data as SeatStatus[]
    );

    const flatSectionsList: FlatVenuePlanSection['sections'] = Object.fromEntries(
      (seatMap.sections || []).map((section) => {
        return [
          section.id,
          {
            id: section.id || '',
            name: section.name,
            capacity: section.seats?.length || 0,
          },
        ];
      })
    );

    const flatSeatsList: FlatVenuePlanSection['seats'] = Object.fromEntries(
      (seatMap.sections || [])
        .map((section) =>
          (section.seats || []).map((seat) => [
            eventSeatStatus[seat.id || ''].id,
            {
              id: eventSeatStatus[seat.id || ''].id,
              section: section.id,
              row: seat.row,
              number: seat.number,
            },
          ])
        )
        .flat()
    );

    return {
      sections: flatSectionsList,
      seats: flatSeatsList,
    };
  } else {
    return null;
  }
}
